/**
 * ChatStore - 聊天状态管理
 *
 * 职责：
 * - 管理所有标签页的聊天历史
 * - 管理输入内容
 * - 管理加载状态
 * - 管理流式输出状态
 *
 * 设计原则：
 * - 单一状态源（Single Source of Truth）
 * - 所有聊天相关状态都在这里
 * - 组件通过storeToRefs读取，通过actions修改
 *
 * 多实例（surface 隔离）：
 * - useChatStore（"chat"）：经典对话布局 / 嵌入模式 / 插件模式
 * - useWorkspaceChatStore（"workspace-chat"）：work 模式（灵枢工作台）
 * 两个实例由同一 setup 工厂创建，输入、历史、流式与选项状态互相独立；
 * 子树内经 useScopedChatStore 按 provide/inject 解析所属实例。
 *
 * @module clinical-record/infrastructure/stores/chatStore
 */

import { defineStore } from "pinia";
import { computed, getCurrentInstance, inject, ref, watch } from "vue";
import type { InjectionKey } from "vue";
import { chatTabKeys, defaultChatTab } from "@/config/productMode";
import { useCapabilityStore } from "@shared/stores";

// ==================== 类型定义 ====================

/** 聊天消息类型 */
export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
  htmlContent?: string;
  sourceMarkdown?: string;
  renderedHtml?: string;
  plainText?: string;
  structuredData?: Record<string, any>;
  format?: "markdown" | "text";
  renderVersion?: string;
  metadata?: Record<string, any>;
}

/** 标签页名称类型 */
export type TabName =
  | "diagnosisTreatment" // 诊断治疗
  | "syndromeDifferentiation" // 理法方药
  | "medicalWriting" // 病历书写
  | "basicHealthCare" // 病历质控
  | "tcmHealthCare" // 健康管理
  | "publicHealthService" // 慢病管理
  | "freeExploration"; // 自由探索（AI自由问答模式保留）

/** 流式输出状态 */
export interface StreamingState {
  messageIndex: number;
  content: string;
  isStreaming: boolean;
  controller: AbortController | null;
}

// ==================== 常量定义 ====================

/** 所有标签页列表 */
export const ALL_TAB_NAMES: TabName[] = [
  "diagnosisTreatment",
  "syndromeDifferentiation",
  "medicalWriting",
  "basicHealthCare",
  "tcmHealthCare",
  "publicHealthService",
  "freeExploration",
] as const;

// ==================== Store 定义 ====================

/**
 * ChatStore 工厂（setup 函数）：同一套状态逻辑生成多个互相独立的实例。
 * 两类对话框（经典布局 / work 模式）的输入、历史、流式与选项状态必须
 * 互相独立；提交流程共用同一套逻辑，经 useScopedChatStore 解析实例。
 */
const setupChatStore = () => {
  // ==================== 权益辅助 ====================

  const capabilityStore = useCapabilityStore();

  /** 首个已授权页签（按页签定义顺序）；一个都没授权时为 null，由 ChatTabs 空态接管 */
  const firstEntitledTab = (): TabName | null => {
    const key = capabilityStore.firstEntitledTab(chatTabKeys);
    return key ? (key as TabName) : null;
  };

  /** 默认页签：未授权时回退到首个已授权页签（无则保持产品默认值） */
  const resolveDefaultActiveTab = (): TabName => {
    const productDefault = defaultChatTab as TabName;
    if (!capabilityStore.loaded || capabilityStore.canUseTab(productDefault)) {
      return productDefault;
    }
    return firstEntitledTab() ?? productDefault;
  };

  // ==================== 状态（State） ====================

  /**
   * 当前激活的标签页
   */
  const activeTab = ref<TabName>(resolveDefaultActiveTab());

  /**
   * 各标签页的聊天历史
   */
  const tabHistories = ref<Record<TabName, ChatMessage[]>>({
    diagnosisTreatment: [],
    syndromeDifferentiation: [],
    medicalWriting: [],
    basicHealthCare: [],
    tcmHealthCare: [],
    publicHealthService: [],
    freeExploration: [],
  });

  /**
   * 各标签页的输入内容
   */
  const inputContents = ref<Record<TabName, string>>({
    diagnosisTreatment: "",
    syndromeDifferentiation: "",
    medicalWriting: "",
    basicHealthCare: "",
    tcmHealthCare: "",
    publicHealthService: "",
    freeExploration: "",
  });

  /**
   * 各标签页的加载状态
   */
  const loadingStates = ref<Record<TabName, boolean>>({
    diagnosisTreatment: false,
    syndromeDifferentiation: false,
    medicalWriting: false,
    basicHealthCare: false,
    tcmHealthCare: false,
    publicHealthService: false,
    freeExploration: false,
  });

  /**
   * 各标签页的流式输出状态
   * 支持多个标签页并行流式传输
   */
  const streamingStates = ref<Record<TabName, StreamingState>>({
    diagnosisTreatment: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
    syndromeDifferentiation: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
    medicalWriting: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
    basicHealthCare: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
    tcmHealthCare: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
    publicHealthService: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
    freeExploration: {
      messageIndex: -1,
      content: "",
      isStreaming: false,
      controller: null,
    },
  });

  /**
   * 提取状态（用于材料提取功能）- 按标签页管理
   */
  const isExtractingStates = ref<Record<TabName, boolean>>({
    diagnosisTreatment: false,
    syndromeDifferentiation: false,
    medicalWriting: false,
    basicHealthCare: false,
    tcmHealthCare: false,
    publicHealthService: false,
    freeExploration: false,
  });

  /**
   * 选项状态（用于单选按钮）
   */
  const selectedOptions = ref<Record<TabName, string>>({
    diagnosisTreatment: "",
    syndromeDifferentiation: "",
    medicalWriting: "",
    basicHealthCare: "",
    tcmHealthCare: "",
    publicHealthService: "",
    freeExploration: "",
  });

  /**
   * 当前批执行的链路追踪标识（仅 work 模式批执行器写入；经典布局恒为空）。
   * 提交流程据此显式附加 X-Trace-ID，不经全局环境槽。
   */
  const activeTraceId = ref("");

  // ==================== 计算属性（Getters） ====================

  /**
   * 当前标签页的聊天历史
   */
  const currentHistory = computed(() => {
    return tabHistories.value[activeTab.value] || [];
  });

  /**
   * 当前标签页的输入内容
   */
  const currentInputContent = computed({
    get: () => inputContents.value[activeTab.value] || "",
    set: (value: string) => {
      inputContents.value[activeTab.value] = value;
    },
  });

  /**
   * 当前标签页是否正在加载
   */
  const isCurrentTabLoading = computed(() => {
    return loadingStates.value[activeTab.value] || false;
  });

  /**
   * 当前标签页的选中选项
   */
  const currentSelectedOption = computed({
    get: () => selectedOptions.value[activeTab.value] || "",
    set: (value: string) => {
      selectedOptions.value[activeTab.value] = value;
    },
  });

  /**
   * 当前标签页是否有历史记录
   */
  const hasCurrentHistory = computed(() => {
    return currentHistory.value.length > 0;
  });

  /**
   * 当前标签页的流式消息索引
   */
  const streamingMessageIndex = computed(() => {
    return streamingStates.value[activeTab.value]?.messageIndex ?? -1;
  });

  /**
   * 当前标签页是否正在流式传输
   */
  const isStreaming = computed(() => {
    return streamingStates.value[activeTab.value]?.isStreaming ?? false;
  });

  /**
   * 当前标签页的流式传输内容
   */
  const currentStreamingContent = computed(() => {
    return streamingStates.value[activeTab.value]?.content ?? "";
  });

  /**
   * 全局提取状态（兼容旧代码）
   */
  const isExtracting = computed({
    get: () => isExtractingStates.value[activeTab.value] || false,
    set: (value: boolean) => {
      isExtractingStates.value[activeTab.value] = value;
    },
  });

  /**
   * 所有标签页的消息总数
   */
  const totalMessageCount = computed(() => {
    return Object.values(tabHistories.value).reduce(
      (sum, history) => sum + history.length,
      0,
    );
  });

  // ==================== 权益联动 ====================

  /**
   * 权益加载完成后校正初始页签：默认页签未授权时回退到首个已授权页签。
   * 一个都没授权时保持默认值，由 ChatTabs 空态提示接管。
   */
  watch(
    () => capabilityStore.loaded,
    (loaded) => {
      if (!loaded || capabilityStore.canUseTab(activeTab.value)) return;
      const fallback = firstEntitledTab();
      if (fallback) {
        activeTab.value = fallback;
      }
    },
  );

  // ==================== Actions（操作方法） ====================

  /**
   * 切换标签页
   *
   * 权益已加载且目标页签未授权时不允许停留：回退到首个已授权页签；
   * 一个都没授权时保持当前页签（由 ChatTabs 空态提示接管）。
   * @param tab 标签页名称
   */
  const setActiveTab = (tab: TabName) => {
    if (capabilityStore.loaded && !capabilityStore.canUseTab(tab)) {
      const fallback = firstEntitledTab();
      if (fallback && fallback !== activeTab.value) {
        console.warn(`标签页 ${tab} 未授权，已切换到 ${fallback}`);
        activeTab.value = fallback;
      }
      return;
    }
    activeTab.value = tab;
  };

  /**
   * 添加消息到指定标签页
   * @param tab 标签页名称
   * @param message 消息对象
   */
  const addMessage = (tab: TabName, message: ChatMessage) => {
    if (!tabHistories.value[tab]) {
      tabHistories.value[tab] = [];
    }
    tabHistories.value[tab].push(message);
  };

  /**
   * 添加消息到当前标签页
   * @param message 消息对象
   */
  const addMessageToCurrentTab = (message: ChatMessage) => {
    addMessage(activeTab.value, message);
  };

  /**
   * 更新指定消息的内容
   * @param tab 标签页名称
   * @param index 消息索引
   * @param content 新内容
   */
  const updateMessage = (tab: TabName, index: number, content: string) => {
    if (tabHistories.value[tab]?.[index]) {
      tabHistories.value[tab][index].content = content;
    }
  };

  /**
   * 更新当前标签页的消息
   * @param index 消息索引
   * @param content 新内容
   */
  const updateCurrentMessage = (index: number, content: string) => {
    updateMessage(activeTab.value, index, content);
  };

  /**
   * 设置标签页的加载状态
   * @param tab 标签页名称
   * @param loading 加载状态
   */
  const setLoading = (tab: TabName, loading: boolean) => {
    loadingStates.value[tab] = loading;
  };

  /**
   * 设置当前标签页的加载状态
   * @param loading 加载状态
   */
  const setCurrentLoading = (loading: boolean) => {
    setLoading(activeTab.value, loading);
  };

  /**
   * 设置标签页的输入内容
   * @param tab 标签页名称
   * @param content 输入内容
   */
  const setInputContent = (tab: TabName, content: string) => {
    inputContents.value[tab] = content;
  };

  /**
   * 设置当前标签页的输入内容
   * @param content 输入内容
   */
  const setCurrentInputContent = (content: string) => {
    setInputContent(activeTab.value, content);
  };

  /**
   * 清空指定标签页的历史记录
   * @param tab 标签页名称
   */
  const clearHistory = (tab: TabName) => {
    tabHistories.value[tab] = [];
  };

  /**
   * 清空当前标签页的历史记录
   */
  const clearCurrentHistory = () => {
    clearHistory(activeTab.value);
  };

  /**
   * 清空所有标签页的历史记录
   */
  const clearAllHistories = () => {
    Object.keys(tabHistories.value).forEach((tab) => {
      tabHistories.value[tab as TabName] = [];
    });
  };

  /**
   * 开始流式输出
   * @param tab 标签页名称
   * @param messageIndex 消息索引
   * @param controller AbortController实例
   */
  const startStreaming = (
    tab: TabName,
    messageIndex: number,
    controller?: AbortController,
  ) => {
    if (!streamingStates.value[tab]) return;

    streamingStates.value[tab].messageIndex = messageIndex;
    streamingStates.value[tab].content = "";
    streamingStates.value[tab].isStreaming = true;
    streamingStates.value[tab].controller = controller || null;
  };

  /**
   * 追加流式输出内容
   * @param tab 标签页名称
   * @param content 内容片段
   */
  const appendStreamingContent = (tab: TabName, content: string) => {
    if (!streamingStates.value[tab]) return;

    streamingStates.value[tab].content += content;

    // 同时更新消息
    const messageIndex = streamingStates.value[tab].messageIndex;
    if (messageIndex >= 0 && tabHistories.value[tab]?.[messageIndex]) {
      tabHistories.value[tab][messageIndex].content =
        streamingStates.value[tab].content;
    }
  };

  /**
   * 停止流式输出
   * @param tab 标签页名称
   */
  const stopStreaming = (tab: TabName) => {
    if (!streamingStates.value[tab]) return;

    streamingStates.value[tab].isStreaming = false;
    streamingStates.value[tab].messageIndex = -1;
    streamingStates.value[tab].content = "";
    streamingStates.value[tab].controller = null;
  };

  /**
   * 取消流式输出
   * @param tab 标签页名称
   */
  const abortStreaming = (tab: TabName) => {
    if (!streamingStates.value[tab]) return;

    const controller = streamingStates.value[tab].controller;
    if (controller) {
      controller.abort();
    }
    stopStreaming(tab);
  };

  /**
   * 取消全部标签页的流式输出并复位加载态（离开所属界面时调用，
   * 防止卸载后的旧流继续回写历史与文库）
   */
  const abortAllStreaming = () => {
    Object.keys(streamingStates.value).forEach((key) => {
      abortStreaming(key as TabName);
    });
    Object.keys(loadingStates.value).forEach((key) => {
      loadingStates.value[key as TabName] = false;
    });
  };

  /**
   * 设置提取状态
   * @param tab 标签页名称
   * @param extracting 是否正在提取
   */
  const setExtracting = (tab: TabName, extracting: boolean) => {
    isExtractingStates.value[tab] = extracting;
  };

  /**
   * 设置当前标签页的提取状态
   * @param extracting 是否正在提取
   */
  const setCurrentExtracting = (extracting: boolean) => {
    setExtracting(activeTab.value, extracting);
  };

  /**
   * 设置选中选项
   * @param tab 标签页名称
   * @param option 选项值
   */
  const setSelectedOption = (tab: TabName, option: string) => {
    selectedOptions.value[tab] = option;
  };

  /**
   * 设置当前标签页的选中选项
   * @param option 选项值
   */
  const setCurrentSelectedOption = (option: string) => {
    setSelectedOption(activeTab.value, option);
  };

  const setActiveTraceId = (traceId: string) => {
    activeTraceId.value = traceId;
  };

  /**
   * 重置所有状态
   */
  const $reset = () => {
    activeTab.value = resolveDefaultActiveTab();
    activeTraceId.value = "";

    Object.keys(tabHistories.value).forEach((key) => {
      tabHistories.value[key as TabName] = [];
    });

    Object.keys(inputContents.value).forEach((key) => {
      inputContents.value[key as TabName] = "";
    });

    Object.keys(loadingStates.value).forEach((key) => {
      loadingStates.value[key as TabName] = false;
    });

    Object.keys(selectedOptions.value).forEach((key) => {
      selectedOptions.value[key as TabName] = "";
    });

    Object.keys(streamingStates.value).forEach((key) => {
      const tab = key as TabName;
      streamingStates.value[tab] = {
        messageIndex: -1,
        content: "",
        isStreaming: false,
        controller: null,
      };
    });

    Object.keys(isExtractingStates.value).forEach((key) => {
      isExtractingStates.value[key as TabName] = false;
    });
  };

  // ==================== 返回 ====================

  return {
    // 状态
    activeTab,
    tabHistories,
    inputContents,
    loadingStates,
    streamingStates,
    isExtracting,
    isExtractingStates,
    selectedOptions,
    activeTraceId,

    // 计算属性
    currentHistory,
    currentInputContent,
    isCurrentTabLoading,
    currentSelectedOption,
    hasCurrentHistory,
    streamingMessageIndex,
    isStreaming,
    currentStreamingContent,
    totalMessageCount,

    // 操作方法
    setActiveTab,
    addMessage,
    addMessageToCurrentTab,
    updateMessage,
    updateCurrentMessage,
    setLoading,
    setCurrentLoading,
    setInputContent,
    setCurrentInputContent,
    clearHistory,
    clearCurrentHistory,
    clearAllHistories,
    startStreaming,
    appendStreamingContent,
    stopStreaming,
    abortStreaming,
    abortAllStreaming,
    setExtracting,
    setCurrentExtracting,
    setSelectedOption,
    setCurrentSelectedOption,
    setActiveTraceId,
    $reset,
  };
};

/**
 * 两个实例的 Store id 显式标注为 string，抹平 Id 字面量差异：
 * 若分别推断为 "chat" / "workspace-chat" 字面量，两个 Store 类型将因
 * $onAction 回调上下文（按 Id 逆变）互不兼容，无法共用 ChatStore 类型
 * 与同一注入键。运行时 $id 仍为对应字符串，看门狗复合键不受影响。
 */
const CHAT_STORE_ID: string = "chat";
const WORKSPACE_CHAT_STORE_ID: string = "workspace-chat";

/** 经典对话布局 / 嵌入模式 / 插件模式的对话框状态（默认实例） */
export const useChatStore = defineStore(CHAT_STORE_ID, setupChatStore);

/** work 模式（灵枢工作台）的对话框状态：独立实例，与经典布局互不影响 */
export const useWorkspaceChatStore = defineStore(
  WORKSPACE_CHAT_STORE_ID,
  setupChatStore,
);

/** ChatStore 实例类型（两个 surface 由同一 setup 工厂创建，形状一致） */
export type ChatStore = ReturnType<typeof useChatStore>;

/** 作用域注入键：work 模式视图 provide，子树组件与组合式函数 inject */
export const CHAT_STORE_KEY: InjectionKey<ChatStore> = Symbol("pgy-chat");

/**
 * 解析当前组件树所属的对话框 Store 实例：
 * - 子树内存在 provide（work 模式）→ 返回提供方实例
 * - 否则（经典 / 嵌入 / 插件布局，或非组件上下文）→ 回退默认实例
 *
 * 注意：inject 只能在 setup 同步阶段解析（本仓库调用点均满足）；
 * 运行时回调（如看门狗定时器）必须持有已解析的实例引用。
 */
export function useScopedChatStore(): ChatStore {
  if (getCurrentInstance()) {
    const scoped = inject<ChatStore | null>(CHAT_STORE_KEY, null);
    if (scoped) return scoped;
  }
  return useChatStore();
}
